import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import moment from 'moment';
import { CompanyIdToCompanyPipe } from '../../companies/company-id-to-company-pipe';
import { CompanyNamePipe } from '../../companies/company-name-pipe';
import { ContractService } from '../contract-service';
import { ContractListItem, toContractListItem } from '../contract.model';
@Component({
  selector: 'app-select-most-recent-contract',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    CompanyNamePipe,
    CompanyIdToCompanyPipe,
  ],
  templateUrl: './select-most-recent-contract.html',
  styleUrl: './select-most-recent-contract.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectMostRecentContract),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectMostRecentContract),
      multi: true,
    },
  ],
})
export class SelectMostRecentContract
  extends RxjsDirective
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  label = input('Contrat précédant');
  showHint = input(false);

  @Input() required: boolean | null | undefined;
  @Input({ required: true }) excludeContractId?: string | null;
  @Input() value: string | null = null;
  @Input({ required: true }) employeeId!: string;
  @Input() startDate: string | null = null;
  @Input() disabled = false;
  contractSelected = output<string | null>();
  contracts: ContractListItem[] = [];

  selectedContract: ContractListItem | null = null;
  private readonly contractService = inject(ContractService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  ngControl?: NgControl;

  compareContracts(a: ContractListItem | null, b: ContractListItem | null): boolean {
    return !!a && !!b && a.id === b.id;
  }

  ngOnInit(): void {
    this.updateContracts();
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['employeeId'] ||
      changes['excludeContractId'] ||
      changes['startDate'] ||
      changes['disabled'] ||
      changes['value']
    ) {
      this.updateContracts();
    }
  }

  updateContracts(): void {
    if (!this.disabled) {
      this.register(
        this.contractService
          .mostRecentsByEmployee(this.employeeId, this.excludeContractId)
          .subscribe((contracts) => {
            this.contracts = contracts
              .filter((c) => c.contractType != 'CDI')
              .filter((c) => c.id !== this.excludeContractId)
              .filter((c) => {
                if (!this.startDate || !c.startDate) {
                  return true;
                }
                return moment(c.startDate).isBefore(this.startDate, 'day');
              });
            if (
              this.value &&
              this.startDate &&
              this.contracts.every((contract) => contract.id !== this.value)
            ) {
              this.value = null;
            }
            this.selectedContract = this.contracts.find((c) => c.id === this.value) ?? null;
            this.cdr.markForCheck();
          }),
      );
    } else if (this.value) {
      this.contractService.findOne(this.value).subscribe((contract) => {
        const contractListItem = toContractListItem(contract);
        this.contracts = [contractListItem];
        this.selectedContract = contractListItem;
        this.cdr.markForCheck();
      });
    }
  }

  onChange: (value: string | null) => void = () => {
    this.contractSelected.emit(this.value);
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string | null): void {
    this.value = value;
    this.selectedContract = this.contracts.find((c) => c.id === value) ?? null;
    this.onChange(this.value);
    this.onTouched();
  }
}
